import type { ReadonlyURLSearchParams } from 'next/navigation';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import type { ElasticSearchResponseFacet } from '@//ElasticSearch';
import { slugify as utilSlugify } from '@//utils/slugify';
import Locations from './known-locations';

/**
 * Given a slug ("stockholm" | "goteborg"), build matching URLSearchParams
 */
export const resolveGeoLocationString = (location: string): URLSearchParams | undefined => {
  if (!Object.hasOwn(Locations, location)) {
    return undefined;
  }

  const { lat, lon, radius, name } = Locations[location];
  const params = new URLSearchParams();
  params.set('lat', lat.toString());
  params.set('lon', lon.toString());
  params.set('position.location', radius.toString());
  params.set('position.name', name);
  params.sort();
  return params;
};

/**
 * Truncate a number to N decimal places (no rounding).
 */
const truncate = (num: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.floor(num * factor) / factor;
};

/**
 * Slugify a display string for comparison and URL segments.
 */
const slugify = (s: string): string => utilSlugify(s, { encode: true });

/**
 * Check if a string is a valid 4-digit model year (1900-2099).
 */
const isValidModelYear = (s: string): boolean => {
  if (!/^\d{4}$/.test(s)) return false;
  const year = parseInt(s, 10);
  return year >= 1900 && year <= 2099;
};

/**
 * Reverse: given URLSearchParams, detect if they match a static location (up to 2 decimals)
 */
export const resolveLocationPathKeyFromParams = (params: URLSearchParams): string | undefined => {
  const latParam = params.get('lat');
  const lonParam = params.get('lon');
  const nameParam = params.get('position.name');
  const radiusParam = params.get('position.location');
  if (!latParam || !lonParam || !nameParam || !radiusParam) return undefined;

  const lat = parseFloat(latParam);
  const lon = parseFloat(lonParam);
  const radius = parseFloat(radiusParam);
  if (Number.isNaN(lat) || Number.isNaN(lon) || Number.isNaN(radius)) return undefined;

  const tLat = truncate(lat, 2);
  const tLon = truncate(lon, 2);
  const normName = slugify(decodeURIComponent(nameParam));

  for (const [slug, loc] of Object.entries(Locations)) {
    if (
      truncate(loc.lat, 2) === tLat &&
      truncate(loc.lon, 2) === tLon &&
      loc.radius === radius &&
      (slugify(loc.name) === normName || normName.startsWith(`${slugify(loc.name)}-`))
    ) {
      return slug;
    }
  }

  return undefined;
};

export const hasValidLocationInParams = (params: URLSearchParams): boolean => {
  const latParam = params.get('lat');
  const lonParam = params.get('lon');
  const nameParam = params.get('position.name');
  const radiusParam = params.get('position.location');
  if (!latParam || !lonParam || !nameParam || !radiusParam) return false;

  const lat = parseFloat(latParam);
  const lon = parseFloat(lonParam);
  if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
  return true;
};

// ---- Constants & Ordering ---------------------------------------------------
const FacetOrder: Record<string, number> = {
  manufacturer: 1,
  modelSeries: 2,
  modelYear: 3,
  gearboxType: 4,
  fuelTypes: 5,
  engineBaseType: 6,
  color: 7,
  chassis: 8,
  drivingWheel: 9,
  branches: 10,
  location: 11,
  'properties.segment': 12,
};
const RangeFacets = new Set<string>(['modelYear']);

const allFacetsOrdered = Object.keys(FacetOrder).sort((a, b) => FacetOrder[a] - FacetOrder[b]);
const sortedRangeFacets = allFacetsOrdered.filter((id) => RangeFacets.has(id));
const sortedNonRangeKeys = allFacetsOrdered.filter(
  (id) => !RangeFacets.has(id) && id !== 'location',
);

/**
 * Extract facet ID from a URL param key.
 * Handles both simple keys (manufacturer) and dotted keys (properties.segment).
 * Only strips .min/.max suffixes for known range facets.
 */
const extractFacetId = (key: string): { id: string; isRangeBound: boolean } => {
  // Check if key ends with .min or .max
  if (key.endsWith('.min') || key.endsWith('.max')) {
    const baseId = key.slice(0, -4); // Remove .min or .max
    // Only treat as range bound if the base ID is a known range facet
    if (RangeFacets.has(baseId)) {
      return { id: baseId, isRangeBound: true };
    }
  }
  // Otherwise, the entire key is the facet ID
  return { id: key, isRangeBound: false };
};

type FacetMap = Map<string, ElasticSearchResponseFacet>;
const buildFacetMap = (facets: ElasticSearchResponseFacet[]): FacetMap =>
  new Map(facets.map((f) => [f.id, f]));

const validateRange = (params: URLSearchParams, seen: Set<string>, id: string): boolean => {
  const minKey = `${id}.min`;
  const maxKey = `${id}.max`;
  const hasMin = params.has(minKey);
  const hasMax = params.has(maxKey);
  if (!hasMin && !hasMax) return true;
  if (!(hasMin && hasMax)) return false;
  const min = params.get(minKey)!;
  const max = params.get(maxKey)!;
  if (min !== max) return false;
  seen.add(id);
  return true;
};

/**
 * Cleans raw query params: retains numeric bounds (when valid), q, offset, hits,
 * known facet keys, and preserves all other params unchanged.
 */
export const cleanParams = (
  raw: URLSearchParams,
  facets: ElasticSearchResponseFacet[],
): URLSearchParams => {
  const clean = new URLSearchParams();
  const facetIds = new Set(facets.map((f) => f.id));

  for (const [key, value] of raw.entries()) {
    const [id, bound] = key.split('.') as [string, 'min' | 'max' | undefined];

    // 1) Numeric bounds
    if (facetIds.has(id) && (bound === 'min' || bound === 'max') && /^\d+$/.test(value)) {
      clean.set(key, value);
      continue;
    }

    // 2) Text or paging
    if (key === 'q' || key === 'offset' || key === 'hits' || key === 'sort') {
      clean.set(key, value);
      continue;
    }

    // 3) Known facets
    if (facetIds.has(key)) {
      clean.append(key, value);
      continue;
    }

    // 4) Unknown params ⇒ preserve unchanged
    clean.append(key, value);
  }

  clean.sort();
  return clean;
};

/**
 * Builds a URL-friendly slug path from query params, validating only against provided facets.
 */
export const facetParamsToPath = (
  params: URLSearchParams,
  facets?: ElasticSearchResponseFacet[],
): string | undefined => {
  if (params.has('q') || params.has('offset')) return;

  const seen = new Set<string>();

  // 1) detect location
  const locKey = resolveLocationPathKeyFromParams(params);
  if (locKey) seen.add('location');

  // 2) validate range facets
  for (const id of sortedRangeFacets) {
    if (!validateRange(params, seen, id)) return;
  }

  // 2.1) detect duplicate non-range facets
  {
    const counts = new Map<string, number>();
    for (const key of params.keys()) {
      const { id, isRangeBound } = extractFacetId(key);
      // skip geo, range bounds, and position params
      if (id === 'lat' || id === 'lon' || id === 'position.location' || id === 'position.name') {
        continue;
      }
      if (isRangeBound) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    // if any "real" facet shows up more than once, bail
    for (const [_, cnt] of counts) {
      if (cnt > 1) return;
    }
  }

  // 3) collect non-range facets
  for (const key of params.keys()) {
    const { id, isRangeBound } = extractFacetId(key);
    if (id === 'lat' || id === 'lon' || key === 'position.location' || key === 'position.name')
      continue;
    if (isRangeBound) continue;
    if (id in FacetOrder && !seen.has(id)) {
      seen.add(id);
    }
  }

  if (facets) {
    const map = buildFacetMap(facets);
    for (const id of seen) {
      if (RangeFacets.has(id) || id === 'location') continue;
      const val = params.get(id)!;
      const meta = map.get(id);
      if (!meta?.filters.some((f) => f.displayName === val)) return;
    }
  }

  return allFacetsOrdered
    .filter((id) => seen.has(id))
    .map((id) => {
      if (id === 'location') return slugify(locKey!);
      if (RangeFacets.has(id)) return slugify(params.get(`${id}.min`)!);
      return slugify(params.get(id)!);
    })
    .join('/');
};

/** Parses a slug path back into query params, with sequential fallback assignments. */
export const pathToFacetParams = (
  path: string,
  facets: ElasticSearchResponseFacet[],
): URLSearchParams => {
  const map = buildFacetMap(facets);
  const params = new URLSearchParams();
  const used = new Set<string>();
  const segments = path.split('/').filter(Boolean);

  let rangeIdx = 0;
  let fallbackIdx = 0;

  for (const raw of segments) {
    const decoded = decodeURIComponent(raw);
    const norm = slugify(decoded);

    //
    // 1) LOCATION SLUGS ANYWHERE IN THE PATH
    //
    if (!used.has('location') && Object.hasOwn(Locations, norm)) {
      const { lat, lon, radius, name } = Locations[norm];
      params.set('lat', String(lat));
      params.set('lon', String(lon));
      params.set('position.location', String(radius));
      params.set('position.name', name);
      used.add('location');
      continue;
    }

    //
    // 2) EXACT FILTER MATCH (prioritize model names like "2008" over years)
    //
    let matched = false;
    for (const id of sortedNonRangeKeys) {
      if (used.has(id)) continue;
      const meta = map.get(id)!;
      const filter = meta.filters.find((f) => slugify(f.displayName) === norm);
      if (filter) {
        params.set(id, filter.displayName);
        used.add(id);
        matched = true;
        break;
      }
    }
    if (matched) continue;

    //
    // 3) NUMERIC → RANGE FACET (only 4-digit years 1900-2099 for modelYear)
    //
    if (isValidModelYear(decoded) && rangeIdx < sortedRangeFacets.length) {
      const id = sortedRangeFacets[rangeIdx++];
      if (!used.has(id)) {
        params.set(`${id}.min`, decoded);
        params.set(`${id}.max`, decoded);
        used.add(id);
      }
      continue;
    }

    //
    // 4) VALIDATED FALLBACK
    //    Only assign if this value really exists in that facet’s filters.
    //
    while (fallbackIdx < sortedNonRangeKeys.length && used.has(sortedNonRangeKeys[fallbackIdx])) {
      fallbackIdx++;
    }
    if (fallbackIdx < sortedNonRangeKeys.length) {
      const id = sortedNonRangeKeys[fallbackIdx++];
      const meta = map.get(id)!;
      // only set it if it truly exists in your ES‐returned filters
      const filter = meta.filters.find((f) => slugify(f.displayName) === norm);
      if (filter) {
        params.set(id, filter.displayName);
        used.add(id);
      }
    }
  }

  return params;
};

/**
 * Validates and normalizes range parameters (*.min / *.max pairs).
 * If min > max, swaps them to ensure valid ranges.
 * Returns normalized params and whether changes were made.
 */
const normalizeRangeParams = (
  params: URLSearchParams,
): { normalized: URLSearchParams; hasChanges: boolean } => {
  const normalized = new URLSearchParams(params);
  let hasChanges = false;

  // Find all range parameter pairs
  const rangeKeys = new Set<string>();
  for (const key of params.keys()) {
    const match = key.match(/^(.+)\.(min|max)$/);
    if (match) {
      rangeKeys.add(match[1]);
    }
  }

  // Validate each range pair
  for (const baseKey of rangeKeys) {
    const minKey = `${baseKey}.min`;
    const maxKey = `${baseKey}.max`;
    const minVal = params.get(minKey);
    const maxVal = params.get(maxKey);

    if (minVal && maxVal) {
      const min = parseFloat(minVal);
      const max = parseFloat(maxVal);

      // Swap if min > max
      if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
        normalized.set(minKey, maxVal);
        normalized.set(maxKey, minVal);
        hasChanges = true;
      }
    }
  }

  return { normalized, hasChanges };
};

interface ServerParamsResolverOptions {
  initialResponse: ElasticVehiclesResponse;
  pathname?: string;
  urlSearchParams: URLSearchParams;
  searchPagePath: string;
}

interface ServerRedirectResponse {
  type: 'redirect';
  redirectUrl: string;
}

interface ServerContinueResponse {
  type: 'continue';
  urlSearchParams: URLSearchParams;
}

export type ServerParamsResolverResult = ServerRedirectResponse | ServerContinueResponse;

/**
 * Server-side logic to clean, normalize, and potentially redirect based on params and slug.
 */
export const serverParamsResolver = ({
  initialResponse,
  pathname,
  urlSearchParams,
  searchPagePath,
}: ServerParamsResolverOptions): ServerParamsResolverResult => {
  const facets = initialResponse.facets;
  const clean = cleanParams(urlSearchParams, facets);

  // Validate and normalize range parameters (swap min/max if needed)
  const { normalized: normalizedParams, hasChanges: rangeChanges } = normalizeRangeParams(clean);
  const slug = pathname?.split('/').filter(Boolean) || [];

  // If range params were swapped, redirect to corrected URL
  if (rangeChanges) {
    const path = slug.length > 0 ? `/${slug.join('/')}` : '';
    const qs = normalizedParams.toString();
    return {
      type: 'redirect',
      redirectUrl: `${searchPagePath}${path}${qs ? `?${qs}` : ''}`,
    };
  }

  // CASE A: no slug → try build slug from params
  if (slug.length === 0) {
    const path = facetParamsToPath(normalizedParams, facets);
    if (path) {
      const usedParams = pathToFacetParams(path, facets);
      const leftover = new URLSearchParams(normalizedParams);
      usedParams.forEach((_v, k) => leftover.delete(k));
      const qs = leftover.toString();
      return {
        type: 'redirect',
        redirectUrl: `${searchPagePath}/${path}${qs ? `?${qs}` : ''}`,
      };
    }
    if (normalizedParams.toString() !== urlSearchParams.toString()) {
      return {
        type: 'redirect',
        redirectUrl: `${searchPagePath}?${normalizedParams.toString()}`,
      };
    }
    return { type: 'continue', urlSearchParams: normalizedParams };
  }

  // CASE B: slug exists → parse back, normalize, merge
  const decodedSlug = slug.map(decodeURIComponent).join('/');
  const fromPath = pathToFacetParams(decodedSlug, facets);
  // pathToFacetParams always returns a URLSearchParams instance, so the
  // previous truthy check was a no-op and the "strip bad segments" fallback
  // below was unreachable. Require at least one resolved param so a slug like
  // "/sok/kjndsasdkjn" — which matches nothing — falls through to the
  // redirect-to-base behaviour instead of being kept in the URL.
  if (fromPath.size > 0) {
    const round = facetParamsToPath(fromPath, facets);
    if (round && round !== decodedSlug) {
      return { type: 'redirect', redirectUrl: `${searchPagePath}/${round}` };
    }
    const merged = new URLSearchParams();
    fromPath.forEach((v, k) => merged.append(k, v));
    const cleanAll = normalizeRangeParams(cleanParams(urlSearchParams, facets)).normalized;
    cleanAll.forEach((v, k) => {
      if (!fromPath.has(k)) merged.append(k, v);
    });
    return { type: 'continue', urlSearchParams: merged };
  }

  // invalid slug → strip bad segments
  const keep: string[] = [];
  let num = 0;
  for (const seg of slug) {
    const decodedSeg = decodeURIComponent(seg);
    const normSeg = slugify(decodedSeg);
    if (isValidModelYear(decodedSeg)) {
      if (num < sortedRangeFacets.length) keep.push(decodedSeg);
      num++;
    } else {
      const id = sortedNonRangeKeys.find((k) =>
        initialResponse.facets
          .find((f) => f.id === k)
          ?.filters.some((fil) => slugify(fil.displayName) === normSeg),
      );
      if (id) keep.push(decodedSeg);
    }
  }
  return {
    type: 'redirect',
    redirectUrl: keep.join('/') ? `${searchPagePath}/${keep.join('/')}` : searchPagePath,
  };
};

/**
 * Client-side resolver: merges path-based and query-based params.
 */
export const clientParamsResolver = ({
  searchParams,
  pathname,
  initialResponse,
  searchPagePath,
}: {
  searchParams: ReadonlyURLSearchParams;
  pathname: string;
  initialResponse: ElasticVehiclesResponse;
  searchPagePath: string;
}): URLSearchParams => {
  const facets = initialResponse.facets;
  const prefix = searchPagePath.replace(/\/+$/, '');
  const segs = pathname.startsWith(`${prefix}/`)
    ? pathname
        .slice(prefix.length + 1)
        .split('/')
        .filter(Boolean)
    : [];

  if (segs.length) {
    const fromPath = pathToFacetParams(segs.join('/'), facets);
    if (fromPath) {
      const cleanAll = cleanParams(new URLSearchParams(searchParams as URLSearchParams), facets);
      const merged = new URLSearchParams();
      fromPath.forEach((v, k) => merged.append(k, v));
      cleanAll.forEach((v, k) => {
        if (!fromPath.has(k)) merged.append(k, v);
      });
      return merged;
    }
  }

  return new URLSearchParams(searchParams as URLSearchParams);
};

/**
 * Removes 'hits', strips offset when zero, and sorts.
 */
export const sanitizeParams = (
  params: URLSearchParams | string,
  options?: { keepOffset?: boolean },
): URLSearchParams => {
  const p = new URLSearchParams(params);
  p.delete('hits');
  p.delete('includeSoldAtFrom');
  if (!options?.keepOffset || p.get('offset') === '0') p.delete('offset');
  p.sort();
  return p;
};

/**
 * Merges two sets of query params, returning both before/after and change flag.
 */
export const mergeUrlSearchParams = (
  currentParams: URLSearchParams | string,
  newParams: URLSearchParams | string,
): {
  mergedParams: URLSearchParams;
  mergedParamsString: string;
  currentParams: URLSearchParams;
  currentParamsString: string;
  containChanges: boolean;
} => {
  const original = new URLSearchParams(currentParams);
  const merged = sanitizeParams(currentParams);
  const toAdd = sanitizeParams(newParams);
  toAdd.forEach((v, k) => {
    if (!merged.getAll(k).includes(v)) merged.append(k, v);
  });
  const finalMerged = sanitizeParams(merged);
  return {
    mergedParams: finalMerged,
    mergedParamsString: finalMerged.toString(),
    currentParams: original,
    currentParamsString: original.toString(),
    containChanges: finalMerged.toString() !== original.toString(),
  };
};

// -- Facets-only extractors --

const ALLOWED_QUERY_KEYS = new Set<string>([
  'q',
  'offset',
  'hits',
  'manufacturer',
  'gearboxType',
  'modelSeries',
  'chassis',
  'color',
  'drivingWheel',
  'environmentClass',
  'properties.segment',
  'branches',
  'inventoryStatus',
  'engineBaseType',
  'fuelTypes',
  'fuelType',
  'price',
  'modelYear',
  'mileage',
  'odometerValueAsKm',
  'deductibleVat',
  'hasManufacturerPackaging',
  'hasMedia',
  'isAuthorizedResellerForManufacturer',
  'mrf',
  'lat',
  'lon',
  'position.location',
  'position.name',
  'ecom',
  'leasing',
  'businessLeasing',
  'isAuctionItem',
  'properties.seats',
  'properties.co2',
  'enginePower',
  'enhancedProperties.electricalRangeWLTP.value',
]);
const RANGE_REGEX = /^(.+)\.(min|max)$/;
const NUMERIC = /^\d+$/;

/**
 * Extract only allowed keys (facets + q + offset), dropping hits=0,
 * and only numeric values for “*.min” / “*.max” ranges.
 */
export const extractQueryForVehicleSearch = (params: URLSearchParams): URLSearchParams => {
  const result = new URLSearchParams();

  for (const [key, value] of params) {
    // 1) drop hits=0
    if (key === 'hits' && value === '0') continue;

    // 2) always allow q, offset, and hits>0
    if (key === 'q' || key === 'offset' || key === 'sort' || (key === 'hits' && value !== '0')) {
      result.append(key, value);
      continue;
    }

    // 3) check for a range parameter like foo.min or foo.max
    const rangeMatch = key.match(RANGE_REGEX);
    if (rangeMatch) {
      const baseKey = rangeMatch[1];
      // allow only if the base key is whitelisted and the value is numeric
      if (ALLOWED_QUERY_KEYS.has(baseKey) && NUMERIC.test(value)) {
        result.append(key, value);
      }
      continue;
    }

    // 4) for non-range facets, only allow if in the whitelist
    if (ALLOWED_QUERY_KEYS.has(key)) {
      result.append(key, value);
    }
  }

  return result;
};

export const extractQueryForVehicleSearchFacetsOnly = (params: URLSearchParams) =>
  extractQueryForVehicleSearch(sanitizeParams(params));

export const getSubscribeableQueryForVehicleSearch = (params: URLSearchParams): URLSearchParams => {
  const queryForSearch = extractQueryForVehicleSearch(params);
  queryForSearch.delete('searchSubscriptionId');
  queryForSearch.delete('offset');
  queryForSearch.delete('hits');
  return queryForSearch;
};
